import { useState, useRef, useEffect, useMemo } from 'react'
import { MdSearch, MdClose } from 'react-icons/md'

export interface SearchableSelectOption {
    value: string
    label: string
    group?: string
}

interface SearchableSelectProps {
    options: SearchableSelectOption[]
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    className?: string
}

export function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = 'Buscar...',
    disabled = false,
    className = '',
}: SearchableSelectProps) {
    const [inputValue, setInputValue] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const selectedOption = useMemo(
        () => options.find(o => o.value === value) || null,
        [options, value]
    )

    const filtered = useMemo(() => {
        if (!inputValue.trim()) return options
        const terms = inputValue.toLowerCase().split(/\s+/)
        return options.filter(o => {
            const text = `${o.value} ${o.label} ${o.group || ''}`.toLowerCase()
            return terms.every(t => text.includes(t))
        })
    }, [options, inputValue])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const item = listRef.current.children[
                highlightedIndex
            ] as HTMLElement
            if (item) item.scrollIntoView({ block: 'nearest' })
        }
    }, [highlightedIndex])

    const handleSelect = (option: SearchableSelectOption) => {
        onChange(option.value)
        setInputValue('')
        setIsOpen(false)
        setHighlightedIndex(-1)
    }

    const handleClear = () => {
        onChange('')
        setInputValue('')
        setIsOpen(false)
        inputRef.current?.focus()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault()
                setIsOpen(true)
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex(prev =>
                    prev < filtered.length - 1 ? prev + 1 : 0
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filtered.length - 1
                )
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
                    handleSelect(filtered[highlightedIndex])
                }
                break
            case 'Escape':
                setIsOpen(false)
                setHighlightedIndex(-1)
                break
        }
    }

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {!isOpen && selectedOption ? (
                <div
                    className="input input-bordered w-full flex items-center cursor-pointer"
                    onClick={() => {
                        if (!disabled) {
                            setIsOpen(true)
                            setTimeout(() => inputRef.current?.focus(), 0)
                        }
                    }}
                >
                    <span className="flex-1 truncate text-sm">
                        {selectedOption.value} - {selectedOption.label}
                    </span>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={e => {
                                e.stopPropagation()
                                handleClear()
                            }}
                            className="btn btn-ghost btn-xs btn-circle shrink-0"
                        >
                            <MdClose className="text-base" />
                        </button>
                    )}
                </div>
            ) : (
                <div className="relative">
                    <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg z-10 pointer-events-none" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={e => {
                            setInputValue(e.target.value)
                            setHighlightedIndex(-1)
                            if (!isOpen) setIsOpen(true)
                        }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsOpen(true)}
                        placeholder={placeholder}
                        disabled={disabled}
                        className="input input-bordered w-full pl-10"
                        autoComplete="off"
                    />
                </div>
            )}

            {isOpen && (
                <ul
                    ref={listRef}
                    className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-xl max-h-72 overflow-y-auto"
                >
                    {filtered.length === 0 ? (
                        <li className="px-4 py-3 text-center text-base-content/60 text-sm">
                            Nenhum resultado encontrado
                        </li>
                    ) : (
                        filtered.map((option, index) => (
                            <li key={option.value}>
                                <button
                                    type="button"
                                    onClick={() => handleSelect(option)}
                                    className={`w-full px-4 py-2.5 text-left hover:bg-base-200 transition-colors border-b border-base-200 last:border-b-0 ${
                                        highlightedIndex === index
                                            ? 'bg-secondary/10'
                                            : ''
                                    } ${
                                        option.value === value
                                            ? 'bg-secondary/5 font-medium'
                                            : ''
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="badge badge-ghost badge-sm font-mono shrink-0 mt-0.5">
                                            {option.value}
                                        </span>
                                        <span className="text-sm leading-snug">
                                            {option.label}
                                        </span>
                                    </div>
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    )
}
